'use client';

import { motion } from 'motion/react';
import { X, ChevronDown } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import countries from 'world-countries';
import { ThankYouModal } from './ThankYouModal';
import { trackFormConversion, getUTMParameters } from '../utils/analytics';
import { detectUserCountry } from '../utils/countryDetection';

interface ContactFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ContactFormModal({ isOpen, onClose }: ContactFormModalProps) {
  const [phone, setPhone] = useState('');
  const [selectedCountryCode, setSelectedCountryCode] = useState('AE');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Function to get flag emoji from country code
  const getFlagEmoji = (countryCode: string) => {
    return countryCode
      .toUpperCase()
      .replace(/./g, char => String.fromCodePoint(127397 + char.charCodeAt(0)));
  };

  // Prepare country options with calling codes
  const countryOptions = countries
    .filter(country => country.idd.root)
    .map(country => ({
      code: country.cca2,
      name: country.name.common,
      dialCode: `${country.idd.root}${country.idd.suffixes?.[0] || ''}`,
      flag: getFlagEmoji(country.cca2)
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  // Filter countries based on search
  const filteredCountries = countryOptions.filter(country =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.dialCode.includes(searchQuery)
  );

  // Get selected country details
  const selectedCountry = countryOptions.find(c => c.code === selectedCountryCode);

  // Detect user's country using IP detection with fallbacks
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const detectedCountry = await detectUserCountry();
        setSelectedCountryCode(detectedCountry);
      } catch (error) {
        console.log('Country detection failed, using default');
        setSelectedCountryCode('AE');
      }
    };
    detectCountry();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const utmParams = getUTMParameters();
      const formDataToSend = new FormData();
      formDataToSend.append('firstName', formData.firstName);
      formDataToSend.append('lastName', formData.lastName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', `${selectedCountry?.dialCode} ${phone}`);
      formDataToSend.append('country', selectedCountry?.name || '');
      formDataToSend.append('formSource', 'Hero Section');
      formDataToSend.append('utm_id', 'LP04');
      formDataToSend.append('utm_source', 'organic_google');
      formDataToSend.append('campaign_name', 'Gate Eleven Landing Page');
      formDataToSend.append('media_source', 'Gate Eleven Residences Website');
      formDataToSend.append('method_of_contact', 'website');
      formDataToSend.append('property_ref_no.', '8478');

      const response = await fetch('https://hooks.zapier.com/hooks/catch/18859173/uwfr4km/', {
        method: 'POST',
        body: formDataToSend,
      });

      if (response.ok) {
        // Track conversion with enhanced data
        trackFormConversion('Contact Modal', formData.email, `${selectedCountry?.dialCode}${phone}`);
        // Reset form
        setFormData({ firstName: '', lastName: '', email: '' });
        setPhone('');
        // Show thank you modal
        setShowThankYou(true);
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/30 "
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-2xl p-8 shadow-2xl max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-black text-xl font-normal">
            Register Your Interest
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors hover:bg-black/5 rounded-full p-2"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <input
                type="text"
                placeholder="E.g.: John"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
                className="w-full bg-gray-50 border-0 text-gray-900 placeholder-gray-400 px-4 py-3.5 text-base rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200 transition-all"
              />
            </div>
            <div>
              <input
                type="text"
                placeholder="E.g.: Doe"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
                className="w-full bg-gray-50 border-0 text-gray-900 placeholder-gray-400 px-4 py-3.5 text-base rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200 transition-all"
              />
            </div>
          </div>

          <div>
            <input
              type="email"
              placeholder="E.g.: john@gmail.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="w-full bg-gray-50 border-0 text-gray-900 placeholder-gray-400 px-4 py-3.5 text-base rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200 transition-all"
            />
          </div>

          <div className="flex gap-2">
            {/* Custom Searchable Country Dropdown */}
            <div ref={dropdownRef} className="relative min-w-[140px]">
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full bg-gray-50 border-0 text-gray-900 px-4 py-3.5 text-base rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200 transition-all flex items-center justify-between"
              >
                <span>{selectedCountry?.flag} {selectedCountry?.dialCode}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg max-h-60 overflow-hidden z-50 shadow-xl">
                  <div className="p-2 border-b border-gray-200">
                    <input
                      type="text"
                      placeholder="Search country..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full bg-gray-50 border-0 text-gray-900 placeholder-gray-400 px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200"
                      autoFocus
                    />
                  </div>
                  <div className="overflow-y-auto max-h-48">
                    {filteredCountries.map((country) => (
                      <button
                        key={country.code}
                        type="button"
                        onClick={() => {
                          setSelectedCountryCode(country.code);
                          setIsDropdownOpen(false);
                          setSearchQuery('');
                        }}
                        className="w-full px-3 py-2 text-left text-gray-900 hover:bg-gray-100 transition-colors flex items-center gap-2 text-sm"
                      >
                        <span>{country.flag}</span>
                        <span className="text-gray-600">{country.dialCode}</span>
                      </button>
                    ))}
                    {filteredCountries.length === 0 && (
                      <div className="px-3 py-4 text-gray-400 text-sm text-center">
                        No countries found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <input
              type="tel"
              placeholder="Mobile Number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              className="flex-1 bg-gray-50 border-0 text-gray-900 placeholder-gray-400 px-4 py-3.5 text-base rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200 transition-all"
            />
          </div>

          {/* Terms and Privacy Notice */}
          <div className="text-center mt-4 mb-2">
            <p className="text-black/60 text-xs">
              By clicking Submit, you agree to our{' '}
              <a href="/terms-and-conditions" className="text-black hover:text-gray-600 underline transition-colors">
                Terms & Conditions
              </a>
              {' '}and{' '}
              <a href="/privacy-policy" className="text-black hover:text-gray-600 underline transition-colors">
                Privacy Policy
              </a>
            </p>
          </div>

          <motion.button
            type="submit"
            disabled={isSubmitting}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            className="w-full bg-black hover:bg-gray-800 text-white px-6 py-4 text-sm font-medium rounded-lg transition-all duration-300 mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </motion.button>
        </form>
      </motion.div>

      {/* Thank You Modal */}
      <ThankYouModal
        isOpen={showThankYou}
        onClose={() => {
          setShowThankYou(false);
          onClose(); // Close the main modal after thank you modal closes
        }}
      />
    </motion.div>
  );
}
